import { Injectable } from '@angular/core';

const SUPPORTED_LOCALES = ['en-ca', 'fr-ca'] as const;
const DEFAULT_LOCALE: LocaleCode = 'en-ca';
const STORAGE_KEY = 'user_lang';

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number];

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  readonly supportedLocales = SUPPORTED_LOCALES;
  readonly defaultLocale = DEFAULT_LOCALE;

  isSupportedLocale(value: string | null | undefined): value is LocaleCode {
    return typeof value === 'string' && this.supportedLocales.includes(value.toLowerCase() as LocaleCode);
  }

  normalizeLocale(value: string | null | undefined): LocaleCode | null {
    if (!value) {
      return null;
    }

    const normalized = value.toLowerCase();
    if (this.isSupportedLocale(normalized)) {
      return normalized;
    }

    const prefix = normalized.split('-')[0];
    if (prefix === 'en') {
      return 'en-ca';
    }
    if (prefix === 'fr') {
      return 'fr-ca';
    }

    return null;
  }

  normalizePreferredLocale(value: string | null | undefined): LocaleCode | null {
    if (!value) {
      return null;
    }

    const normalized = value.toLowerCase().trim();

    if (this.isSupportedLocale(normalized)) {
      return normalized;
    }

    if (normalized === 'en') {
      return 'en-ca';
    }

    if (normalized === 'fr') {
      return 'fr-ca';
    }

    return null;
  }

  getLocaleFromPath(pathname: string = window.location.pathname): LocaleCode | null {
    const match = pathname.match(/^\/(en-ca|fr-ca)(?=\/|$)/i);
    return this.normalizeLocale(match?.[1] ?? null);
  }

  getLocaleFromUrl(pathname: string = window.location.pathname): LocaleCode | null {
    const prefixedLocale = this.getLocaleFromPath(pathname);
    if (prefixedLocale) {
      return prefixedLocale;
    }
    if (/^\/envois(?:\/|$)/i.test(pathname)) {
      return 'fr-ca';
    }

    if (/^\/shipments(?:\/|$)/i.test(pathname)) {
      return 'en-ca';
    }

    return null;
  }

  getCurrentLocale(): LocaleCode {
    return this.getLocaleFromUrl() ?? this.resolvePreferredLocale();
  }

  resolvePreferredLocale(preferredLanguage?: string | null): LocaleCode {
    const explicitLocale = this.normalizePreferredLocale(preferredLanguage ?? null);
    if (explicitLocale) {
      return explicitLocale;
    }

    const storedLocale = this.normalizePreferredLocale(this.readStoredLocale());
    if (storedLocale) {
      return storedLocale;
    }

    return this.defaultLocale;
  }

  setPreferredLocale(preferredLanguage: string | null | undefined): LocaleCode {
    const locale = this.normalizePreferredLocale(preferredLanguage) ?? this.defaultLocale;

    try {
      sessionStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Session storage may be unavailable in restricted browser contexts.
    }

    return locale;
  }

  withLocalePath(path: string, locale?: string | null): string {
    const targetLocale = this.normalizeLocale(locale ?? null) ?? this.resolvePreferredLocale();
    const { pathOnly, suffix } = this.splitPathAndSuffix(path);
    const normalizedPath = this.stripSupportedLocalePrefix(pathOnly);
    const baseHrefLocale = this.getBaseHrefLocale();

    if (baseHrefLocale && baseHrefLocale === targetLocale) {
      return `${normalizedPath}${suffix}`;
    }

    const joinedPath = normalizedPath === '/' ? '' : normalizedPath;

    return `/${targetLocale}${joinedPath}${suffix}`;
  }

  stripSupportedLocalePrefix(path: string): string {
    const { pathOnly, suffix } = this.splitPathAndSuffix(path);
    const withoutPrefix = pathOnly.replace(/^\/(en-ca|fr-ca)(?=\/|$)/i, '');
    const normalizedPath = withoutPrefix.startsWith('/') ? withoutPrefix : `/${withoutPrefix}`;

    return `${normalizedPath === '' ? '/' : normalizedPath}${suffix}`;
  }

  buildLocalizedAbsoluteUrl(targetLocale: string, currentUrl: string): string {
    const localizedPath = this.buildLocalePrefixedPath(currentUrl, targetLocale);
    return `${window.location.origin}${localizedPath}`;
  }

  getApiLanguage(locale?: string | null): 'en' | 'fr' {
    const currentLocale = this.normalizeLocale(locale ?? null) ?? this.getCurrentLocale();
    return currentLocale === 'fr-ca' ? 'fr' : 'en';
  }

  getApiPayloadLanguage(locale?: string | null): 'EN' | 'FR' {
    const currentLocale = this.normalizeLocale(locale ?? null) ?? this.getCurrentLocale();
    return currentLocale === 'fr-ca' ? 'FR' : 'EN';
  }

  private readStoredLocale(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private splitPathAndSuffix(path: string): { pathOnly: string; suffix: string } {
    const queryIndex = path.indexOf('?');
    const hashIndex = path.indexOf('#');

    let suffixStart = -1;
    if (queryIndex >= 0 && hashIndex >= 0) {
      suffixStart = Math.min(queryIndex, hashIndex);
    } else {
      suffixStart = queryIndex >= 0 ? queryIndex : hashIndex;
    }

    if (suffixStart < 0) {
      return {
        pathOnly: path || '/',
        suffix: ''
      };
    }

    return {
      pathOnly: path.substring(0, suffixStart) || '/',
      suffix: path.substring(suffixStart)
    };
  }

  private buildLocalePrefixedPath(path: string, locale?: string | null): string {
    const targetLocale = this.normalizeLocale(locale ?? null) ?? this.resolvePreferredLocale();
    const { pathOnly, suffix } = this.splitPathAndSuffix(path);
    const normalizedPath = this.stripSupportedLocalePrefix(pathOnly);
    const joinedPath = normalizedPath === '/' ? '' : normalizedPath;

    return `/${targetLocale}${joinedPath}${suffix}`;
  }

  private getBaseHrefLocale(): LocaleCode | null {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return null;
    }

    const baseHref = document.querySelector('base')?.getAttribute('href')?.trim();
    if (!baseHref) {
      return null;
    }

    try {
      const pathname = new URL(baseHref, window.location.origin).pathname;
      return this.getLocaleFromPath(pathname);
    } catch {
      return this.getLocaleFromPath(baseHref);
    }
  }

}